
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { StepProps } from './types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface SuccessMessageProps {
  userName: string;
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({ userName }) => (
  <div className="text-center py-8">
    <div className="mb-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Ọmọ Odùduwà, {userName}!</h2>
      <div className="text-lg text-gray-700 space-y-2">
        <p>Thank you for registering with us.</p>
        <p className="font-semibold text-omoduduwa-indigo">We must unite in 1 voice to choose better leaders going forward.</p>
        <p>Your registration is being reviewed and you'll be notified once approved.</p>
        <p className="text-sm text-gray-600 mt-4">
          Together, we build a stronger community for our people.
        </p>
      </div>
    </div>
  </div>
);

const ProfileReviewStep: React.FC<StepProps> = ({ data, onPrevious }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      console.log('[ProfileReviewStep] Starting registration process...');
      
      // Step 1: Create user account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
      });

      if (authError) {
        console.error('[ProfileReviewStep] Auth error:', authError);
        if (authError.message.includes('already registered')) {
          toast.error('An account with this email already exists. Please try logging in instead.');
        } else {
          toast.error(`Registration failed: ${authError.message}`);
        }
        return;
      }

      if (!authData.user) {
        console.error('[ProfileReviewStep] No user returned from signup');
        toast.error('Registration failed - no user created');
        return;
      }

      console.log('[ProfileReviewStep] User created:', authData.user.id);

      // Step 2: Create member record with all new fields
      const memberData = {
        user_id: authData.user.id,
        full_name: data.fullName,
        username: data.username,
        email: data.email,
        phone_number: data.phoneNumber,
        gender: data.gender,
        date_of_birth: data.dateOfBirth ? format(data.dateOfBirth, 'yyyy-MM-dd') : null,
        state: data.state,
        local_government: data.localGovernment,
        ward: data.ward || null,
        occupation: data.occupation,
        association: data.association || null,
        referral_code: data.referralCode || null,
        approved: false
      };

      console.log('[ProfileReviewStep] Creating member record:', memberData);

      const { error: memberError } = await supabase
        .from('members')
        .insert([memberData]);

      if (memberError) {
        console.error('[ProfileReviewStep] Member creation error:', memberError);
        toast.error(`Database error: ${memberError.message}`);
        return;
      }

      console.log('[ProfileReviewStep] Registration completed successfully');
      
      // Show success message
      setShowSuccess(true);
      
      // Auto-redirect after 8 seconds
      setTimeout(() => {
        navigate('/community');
      }, 8000);

    } catch (error) {
      console.error('[ProfileReviewStep] Unexpected error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return <SuccessMessage userName={data.fullName} />;
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-center text-2xl font-bold text-gray-900">
          Review Your Information
        </CardTitle>
        <p className="text-center text-gray-600">Step 4 of 4 - Please review before submitting</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium">Full Name:</span> {data.fullName}</div>
            <div><span className="font-medium">Username:</span> {data.username}</div>
            <div><span className="font-medium">Phone:</span> {data.phoneNumber}</div>
            <div><span className="font-medium">Email:</span> {data.email}</div>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium">Gender:</span> {data.gender}</div>
            <div><span className="font-medium">Date of Birth:</span> {data.dateOfBirth ? format(data.dateOfBirth, 'PPP') : 'Not provided'}</div>
            <div><span className="font-medium">State:</span> {data.state}</div>
            <div><span className="font-medium">LGA:</span> {data.localGovernment}</div>
            {data.ward && <div><span className="font-medium">Ward:</span> {data.ward}</div>}
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Movement Details</h3>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div><span className="font-medium">Occupation:</span> {data.occupation}</div>
            {data.association && <div><span className="font-medium">Association:</span> {data.association}</div>}
            {data.referralCode && <div><span className="font-medium">Referral Code:</span> {data.referralCode}</div>}
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <Button onClick={onPrevious} variant="outline" className="flex-1">
            Previous
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading} className="flex-1">
            {isLoading ? 'Creating Account...' : 'Join Ọmọ Odùduwà'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileReviewStep;
